//
import React, { Component } from "react"
import PropTypes from "prop-types"
import { connect } from "react-redux"
import { createStructuredSelector } from "reselect"
// https://redux.js.org/recipes/using-immutablejs-with-redux/#use-a-higher-order-component-to-convert-your-smart-components-immutablejs-props-to-your-dumb-components-javascript-props
import withImmutablePropsToJS from "with-immutable-props-to-js"

import FilterGroup from "shared/search/containers/FilterGroup/index"
import { SearchFilterGroup } from "app/static/frontend/search-new/components/SearchComponents/SearchFilterGroup"

import {
    AddedIdsFilter,
    AdvancedSearchFilter,
    AssignedByUserFilter,
    AssignedByUserToMeFilter,
    AssignedToMeFilter,
    AssignedToSupporterFilter,
    AssignedToUserFilter,
    NotAssignedToUserFilter,
    NotAssignedToSupporterFilter,
    NotAssignedToMeFilter,
    AssignmentTypeFilter,
    DeletedIdsFilter,
    HasAssignmentTripletFilter,
    AssignmentOrderByCreatorNameFilter,
    AssignmentOrderByUserNameFilter,
    AssignmentOrderByContactNameFilter,
    AssignmentOrderByAmendmentTitleFilter,
    AssignmentOrderByBillTitleFilter,
    AssignmentOrderByCustomEventNameFilter,
    AssignmentOrderByDocketTitleFilter,
    AssignmentOrderByOfficialLastnameFilter,
    AssignmentOrderByProjectNameFilter,
    AssignmentOrderByPublicOrganizationNameFilter,
    AssignmentOrderByRegulationTitleFilter,
    AssignmentOrderBySupporterLastnameFilter,
    AssignmentOrderByVoteQuestionFilter,
    OnlyIdsFilter,
    RegionFilter,
    StateRegionFilter,
    RegionNoFederalFilter,
    createOrderByFilter,
    InvisibleUnreviewedByCriteriaFilter,
    InvisibleExcludeFiltersFilter,
    InvisibleNotReviewedByTeamsFilter,
    createTransactionAmountFilter,
} from "shared/search/containers/Filter/Filters"

// helpers
import { isFeatureEnabled } from "shared/featureflags/helperFunctions"

// selectors
import { selectHasMultipleBankAccounts } from "shared/pac/selectors"
import { checkHasPermission } from "shared/imports/permissionFunctions"

export const genericFilterGroup = (props) => {
    const FilterGroupComponent = isFeatureEnabled("ff_filter_refactor") ? SearchFilterGroup : FilterGroup

    return (
        <FilterGroupComponent
            uniqueFilterGroupId={props.uniqueFilterGroupId}
            filters={props.filters}
            displayLabel={props.displayLabel}
            {...props}
        />
    )
}

genericFilterGroup.propTypes = {
    regions: PropTypes.arrayOf(PropTypes.number),
    permissions: PropTypes.arrayOf(PropTypes.number),
}
export default genericFilterGroup

/* *****************************************************************************
 * Commonly Used Filter Groups
 ****************************************************************************** */

export class AdvancedSearchFilterGroup extends Component {
    static defaultProps = {
        filters: [AdvancedSearchFilter],
        uniqueFilterGroupId: "advancedSearchFilterGroup",
        noHeader: true,
        alwaysOpen: true,
    }

    render() {
        return genericFilterGroup(this.props)
    }
}

export class RegionFilterGroup extends Component {
    static defaultProps = {
        filters: [RegionFilter],
        uniqueFilterGroupId: "regionFilterGroup",
        displayLabel: "Region",
        helpText: "Filter results by a single region or a group of regions.",
    }

    render() {
        return genericFilterGroup(this.props)
    }
}

export class StateRegionFilterGroup extends Component {
    static defaultProps = {
        filters: [StateRegionFilter],
        uniqueFilterGroupId: "stateRegionFilterGroup",
        displayLabel: "Region",
        helpText: "Filter results by a single state or a group of states.",
    }

    render() {
        return genericFilterGroup(this.props)
    }
}

export const RegionNoFederalFilterGroup = (props) => genericFilterGroup(props)
RegionNoFederalFilterGroup.defaultProps = {
    filters: [RegionNoFederalFilter],
    uniqueFilterGroupId: "regionNoFederalFilterGroup",
    displayLabel: "Region",
    helpText: "Filter results by a single region or a group of regions.",
}

export const AssignmentFilterGroup = (props) => genericFilterGroup(props)
AssignmentFilterGroup.propTypes = {
    filters: PropTypes.array,
    uniqueFilterGroupId: PropTypes.string,
    displayLabel: PropTypes.string,
    helpText: PropTypes.string,
}
AssignmentFilterGroup.defaultProps = {
    filters: [
        AssignedToUserFilter,
        AssignedToSupporterFilter,
        AssignedToMeFilter,
        NotAssignedToUserFilter,
        NotAssignedToSupporterFilter,
        NotAssignedToMeFilter,
        HasAssignmentTripletFilter,
    ],
    uniqueFilterGroupId: "assignmentFilterGroup",
    displayLabel: "Assignments",
    helpText: "Filter results by which member of your team, if any, has been " + "assigned to them.",
}

export const AssignmentTypeFilterGroup = (props) => genericFilterGroup(props)
AssignmentTypeFilterGroup.defaultProps = {
    filters: [AssignmentTypeFilter],
    displayLabel: "Assignment Types",
    uniqueFilterGroupId: "assignmentTypeFilterGroup",
}

export const AssignmentCreatorFilterGroup = (props) => genericFilterGroup(props)
AssignmentCreatorFilterGroup.defaultProps = {
    filters: [AssignedByUserFilter, AssignedByUserToMeFilter],
    displayLabel: "Assigned By",
    uniqueFilterGroupId: "assignmentCreatorFilterGroup",
}

export const AssignmentOrderingFilterGroup = (props) => genericFilterGroup(props)
AssignmentOrderingFilterGroup.defaultProps = {
    filters: [
        AssignmentOrderByCreatorNameFilter,
        AssignmentOrderByUserNameFilter,
        AssignmentOrderByContactNameFilter,
        AssignmentOrderByAmendmentTitleFilter,
        AssignmentOrderByBillTitleFilter,
        AssignmentOrderBySupporterLastnameFilter,
        AssignmentOrderByCustomEventNameFilter,
        AssignmentOrderByDocketTitleFilter,
        AssignmentOrderByOfficialLastnameFilter,
        AssignmentOrderByProjectNameFilter,
        AssignmentOrderByPublicOrganizationNameFilter,
        AssignmentOrderByRegulationTitleFilter,
        AssignmentOrderByVoteQuestionFilter,
    ],
    displayLabel: "Ordering",
    uniqueFilterGroupId: "assignmentOrderingFilterGroup",
}

export const InvisibleDefaultFilterGroup = (props) => genericFilterGroup(props)
InvisibleDefaultFilterGroup.defaultProps = {
    filters: [AddedIdsFilter, DeletedIdsFilter, OnlyIdsFilter],
    uniqueFilterGroupId: "invisibleDefaultFilterGroup",
    invisible: true,
}

export const FromTrackingBoardInvisibleFilterGroup = (props) => genericFilterGroup(props)
FromTrackingBoardInvisibleFilterGroup.defaultProps = {
    filters: [InvisibleUnreviewedByCriteriaFilter, InvisibleExcludeFiltersFilter, InvisibleNotReviewedByTeamsFilter],
    uniqueFilterGroupId: "fromTrackingBoardInvisibleFilterGroup",
    invisible: true,
}

export const createOrderByFilterIdFromQDT = (quorumDataType) => {
    const qdtKey = DjangIO.app.models.QuorumDataType.by_value(quorumDataType).key
    return `${qdtKey}OrderBy`
}

export const createOrderByFilterGroup = ({ quorumDataType }) => {
    const OrderByFilterGroup = (props) => genericFilterGroup(props)
    const uniqueFilterId = createOrderByFilterIdFromQDT(quorumDataType)

    OrderByFilterGroup.defaultProps = {
        filters: [
            createOrderByFilter({
                uniqueFilterId,
            }),
        ],
        uniqueFilterId: uniqueFilterId,
        uniqueFilterGroupId: `${uniqueFilterId}FilterGroup`,
        invisible: true,
    }

    return OrderByFilterGroup
}

const parseAndGeneratePACFilters = ({ hasMultipleBankAccounts, quorumDataType }) =>
    checkHasPermission([DjangIO.app.models.PermissionType.qpac_v1])
        ? [
              createTransactionAmountFilter({
                  hasMultipleBankAccounts,
                  quorumDataType,
              }),
          ]
        : []

// see
// shared/search/containers/FilterGroup/CustomFilterGroup.js
// shared/search/containers/FilterGroup/SupporterPACFilterGroup.js
// for similar examples of connected filter groups/filters
export const createPACTransactionsFilterGroup = ({ displayLabel, quorumDataType, uniqueFilterGroupId }) => {
    const mapStateToProps = createStructuredSelector({
        hasMultipleBankAccounts: selectHasMultipleBankAccounts,
    })
    const actions = {}

    class UnconnectedPACTransactionsFilterGroup extends Component {
        render() {
            const canViewDisbursementsOrReceipts =
                !isFeatureEnabled("ff_pac_permissions_one") ||
                Userdata.can_view_pac_receipts ||
                Userdata.can_view_pac_disbursements
            if (!canViewDisbursementsOrReceipts) return null

            return genericFilterGroup({
                uniqueFilterGroupId,
                filters: [
                    ...(checkHasPermission([DjangIO.app.models.PermissionType.qpac_v1])
                        ? [
                              createTransactionAmountFilter({
                                  hasMultipleBankAccounts: this.props.hasMultipleBankAccounts,
                                  quorumDataType,
                              }),
                          ]
                        : []),
                ],
                displayLabel,
                helpText: "Filter by transactions.",
                ...this.props,
            })
        }
    }

    UnconnectedPACTransactionsFilterGroup.propTypes = {
        hasMultipleBankAccounts: PropTypes.bool.isRequired,
        uniqueFilterGroupId: PropTypes.string.isRequired,
        uniqueFlvId: PropTypes.string.isRequired,
    }

    const connectedFilterGroup = connect(
        mapStateToProps,
        actions,
    )(withImmutablePropsToJS(UnconnectedPACTransactionsFilterGroup))
    Object.assign(connectedFilterGroup, {
        defaultProps: {
            uniqueFilterGroupId,
            isConnectedFilterGroup: true,
            parseFunction: parseAndGeneratePACFilters,
        },
    })

    return connectedFilterGroup
}
